import React, { useState } from 'react';
import { Form, Input, Button, Steps, Select, Segmented, Checkbox, Upload, message } from 'antd';
import { UserOutlined, ShopOutlined, PlusOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';

import MainLayout from '../../components/Layout/MainLayout';
import { useAuth } from '../../context/AuthContext';

import {
  PageWrapper,
  PageTitle,
  TypeSelectorWrapper,
  StepsWrapper,
  FormSection,
  StepTitle,
  ButtonRow,
} from './styles';

const { Option } = Select;
const { TextArea } = Input;

const CR_PROVINCES = [
  'San José',
  'Alajuela',
  'Cartago',
  'Heredia',
  'Guanacaste',
  'Puntarenas',
  'Limón',
];

export default function SignUp() {
  const [registrationType, setRegistrationType] = useState('Comprador');
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const { register } = useAuth();
  const history = useHistory();

  const isSeller = registrationType === 'Vendedor';

  const steps = [
    { title: 'Información Personal' },
    { title: 'Información de Pago' },
    ...(isSeller ? [{ title: 'Información de Tienda' }] : []),
    { title: 'Credenciales' },
  ];

  const handleNext = () => {
    form.validateFields().then(() => {
      setCurrentStep((prev) => prev + 1);
    }).catch(() => {});
  };

  const handlePrev = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }

    const values = form.getFieldsValue(true);
    const fullName = [values.nombre, values.apellido].filter(Boolean).join(' ');

    const payload = {
      name: fullName,
      email: values.email,
      password: values.contraseña,
      isSeller,
      isBuyer: !isSeller,
    };

    if (isSeller && values.nombreTienda) {
      payload.storeName = values.nombreTienda;
      payload.storeDescription = values.descripcionTienda || '';
    }

    setSubmitting(true);
    try {
      await register(payload);
      history.push('/cuenta');
    } catch (err) {
      message.error(err.message || 'No se pudo crear la cuenta');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTypeChange = (value) => {
    setRegistrationType(value);
    setCurrentStep(0);
  };

  const isLastStep = currentStep === steps.length - 1;

  const renderStepContent = () => {
    const stepKey = isSeller
      ? ['personal', 'payment', 'store', 'credentials'][currentStep]
      : ['personal', 'payment', 'credentials'][currentStep];

    switch (stepKey) {
      case 'personal':
        return (
          <FormSection>
            <StepTitle>Información Personal</StepTitle>
            <Form.Item
              name="nombre"
              label="Nombre"
              rules={[{ required: true, message: 'Ingresa tu nombre' }]}
            >
              <Input prefix={<UserOutlined />} placeholder="Tu nombre" />
            </Form.Item>
            <Form.Item
              name="apellido"
              label="Apellido"
              rules={[{ required: true, message: 'Ingresa tu apellido' }]}
            >
              <Input placeholder="Tu apellido" />
            </Form.Item>
            <Form.Item
              name="teléfono"
              label="Teléfono"
              rules={[{ required: true, message: 'Ingresa tu teléfono' }]}
            >
              <Input placeholder="8888-8888" />
            </Form.Item>
            <Form.Item
              name="dirección"
              label="Dirección"
              rules={[{ required: true, message: 'Ingresa tu dirección' }]}
            >
              <Input placeholder="Tu dirección" />
            </Form.Item>
            <Form.Item
              name="provincia"
              label="Provincia"
              rules={[{ required: true, message: 'Selecciona tu provincia' }]}
            >
              <Select placeholder="Selecciona una provincia">
                {CR_PROVINCES.map((prov) => (
                  <Option key={prov} value={prov}>{prov}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="ciudad"
              label="Ciudad"
              rules={[{ required: true, message: 'Ingresa tu ciudad' }]}
            >
              <Input placeholder="Tu ciudad" />
            </Form.Item>
          </FormSection>
        );

      case 'payment':
        return (
          <FormSection>
            <StepTitle>Información de Pago</StepTitle>
            <Form.Item name="tipoTarjeta" label="Tipo de tarjeta">
              <Select placeholder="Selecciona tipo de tarjeta">
                <Option value="visa">Visa</Option>
                <Option value="mastercard">Mastercard</Option>
                <Option value="amex">American Express</Option>
              </Select>
            </Form.Item>
            <Form.Item name="numeroTarjeta" label="Número de tarjeta">
              <Input placeholder="0000 0000 0000 0000" />
            </Form.Item>
            <Form.Item name="fechaVencimiento" label="Fecha de vencimiento">
              <Input placeholder="MM/AA" />
            </Form.Item>
            <Form.Item name="cvv" label="CVV">
              <Input placeholder="000" />
            </Form.Item>
          </FormSection>
        );

      case 'store':
        return (
          <FormSection>
            <StepTitle>Información de Tienda</StepTitle>
            <Form.Item
              name="nombreTienda"
              label="Nombre de tienda"
              rules={[{ required: true, message: 'Ingresa el nombre de tu tienda' }]}
            >
              <Input prefix={<ShopOutlined />} placeholder="Nombre de tu tienda" />
            </Form.Item>
            <Form.Item
              name="descripcionTienda"
              label="Descripción de tienda"
              rules={[{ required: true, message: 'Ingresa una descripción' }]}
            >
              <TextArea rows={4} placeholder="Describe tu tienda..." />
            </Form.Item>
            <Form.Item name="imagenTienda" label="Imagen de tienda">
              <Upload
                listType="picture-card"
                maxCount={1}
                beforeUpload={() => false}
              >
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Subir imagen</div>
                </div>
              </Upload>
            </Form.Item>
          </FormSection>
        );

      case 'credentials':
        return (
          <FormSection>
            <StepTitle>Credenciales</StepTitle>
            <Form.Item
              name="email"
              label="Correo electrónico"
              rules={[
                { required: true, message: 'Ingresa tu correo electrónico' },
                { type: 'email', message: 'Ingresa un correo válido' },
              ]}
            >
              <Input placeholder="tu@email.com" />
            </Form.Item>
            <Form.Item
              name="contraseña"
              label="Contraseña"
              rules={[{ required: true, message: 'Ingresa tu contraseña' }]}
            >
              <Input.Password placeholder="Tu contraseña" />
            </Form.Item>
            <Form.Item
              name="confirmarContraseña"
              label="Confirmar contraseña"
              rules={[{ required: true, message: 'Confirma tu contraseña' }]}
            >
              <Input.Password placeholder="Confirma tu contraseña" />
            </Form.Item>
            <Form.Item
              name="terminos"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) => (value
                    ? Promise.resolve()
                    : Promise.reject(new Error('Debes aceptar los términos y condiciones'))),
                },
              ]}
            >
              <Checkbox>Acepto los términos y condiciones</Checkbox>
            </Form.Item>
          </FormSection>
        );

      default:
        return null;
    }
  };

  return (
    <MainLayout showSidebar={false}>
      <PageWrapper>
        <PageTitle>Crear cuenta</PageTitle>

        <TypeSelectorWrapper>
          <Segmented
            options={[
              { label: 'Comprador', value: 'Comprador', icon: <UserOutlined /> },
              { label: 'Vendedor', value: 'Vendedor', icon: <ShopOutlined /> },
            ]}
            value={registrationType}
            onChange={handleTypeChange}
            size="large"
          />
        </TypeSelectorWrapper>

        <StepsWrapper>
          <Steps current={currentStep} items={steps} />
        </StepsWrapper>

        <Form form={form} layout="vertical">
          {renderStepContent()}

          <ButtonRow>
            {currentStep > 0 ? (
              <Button size="large" onClick={handlePrev}>
                Anterior
              </Button>
            ) : (
              <div />
            )}

            {isLastStep ? (
              <Button type="primary" size="large" onClick={handleSubmit} loading={submitting}>
                Crear cuenta
              </Button>
            ) : (
              <Button type="primary" size="large" onClick={handleNext}>
                Siguiente
              </Button>
            )}
          </ButtonRow>
        </Form>
      </PageWrapper>
    </MainLayout>
  );
}
